import type {RoundingTargetNode} from '@/pages/components/fs-report/RoundingAdjustDialog';
import type {
    RoundingAdjustGroupVO,
    RoundingAdjustTarget,
    RoundingAdjustTargetBO,
} from '@/services/auditService/roundingAdjustService';
import {emptyRoundingAdjustGroup} from '@/services/auditService/roundingAdjustService';
import {FS_CODE, normalizeCode} from '@/utils/fsCodes';

export type {RoundingAdjustGroupVO, RoundingAdjustTarget, RoundingAdjustTargetBO};
export {emptyRoundingAdjustGroup};

const PL_EXPENSE_PARENTS = new Set(['PL-COT', 'PL-AE', 'PL-FC']);
const BS_ASSET_PARENTS = new Set(['BS-NCA', 'BS-CA']);
const BS_LIABILITY_PARENTS = new Set(['BS-CL', 'BS-NCL']);

export interface RoundingNodeImpact {
    netAssetsDelta: number;
    eqDelta: number;
}

export interface RoundingAdjustmentMaps {
    /** BalanceSheet 显示用：BS 节点直接映射，PL 汇总到 BS-EQ-LFP */
    bsDisplayMap: Record<string, number>;
    /** ProfitLoss 显示用：PL 节点 targetCode 映射 */
    plDisplayMap: Record<string, number>;
    /** 各 displayCode 来源 BS / PL */
    sourceMap: Record<string, 'BS' | 'PL'>;
    /** 弹窗回显：roundingNodeKey → adjustAmount（MULTI·PL 为 code@YOA） */
    savedNodeValues: Record<string, number>;
}

export interface RoundingApplyEntry {
    node: RoundingTargetNode;
    adjustAmount: number;
}

export interface BuildRoundingMapsOptions {
    /**
     * 过滤 PL targets 的 YOA。
     * - 传入单片 YOA：仅该片 PL 进入 plDisplayMap / LFP 贡献（PL Tab / MULTI draft）
     * - 不传：全部 PL targets（BS engagement 显示需跨年汇总 LFP）
     */
    yearOfAssessment?: string | null;
}

/** 规范化 YOA；空 → null */
export function normalizeRoundingYoa(yoa: string | null | undefined): string | null {
    const s = String(yoa ?? '').trim();
    return s ? s : null;
}

/**
 * 弹窗 / 回显用节点键：仅 MULTI·PL（带 YOA）用 code@YOA；其余与改造前一致用 code。
 */
export function roundingNodeKey(
    node: Pick<RoundingTargetNode, 'code' | 'statement' | 'yearOfAssessment'>
): string {
    const code = String(node.code || '');
    const yoa = normalizeRoundingYoa(node.yearOfAssessment);
    if (node.statement === 'PL' && yoa) {
        return `${code}@${yoa}`;
    }
    return code;
}

/** 候选节点是否含 MULTI·PL YOA（决定是否走复合键） */
function nodesUseYoaKeys(targetNodes: RoundingTargetNode[]): boolean {
    return targetNodes.some(n => n.statement === 'PL' && !!normalizeRoundingYoa(n.yearOfAssessment));
}

function targetMatchesYoaFilter(
    target: RoundingAdjustTarget,
    yoaFilter: string | null | undefined
): boolean {
    if (yoaFilter == null || yoaFilter === '') return true;
    if (target.targetStatement !== 'PL') return true;
    const targetYoa = normalizeRoundingYoa(target.yearOfAssessment);
    // legacy 无 YOA 的 PL：在指定片过滤时不计入（避免串年）
    if (!targetYoa) return false;
    return targetYoa === yoaFilter;
}

/**
 * 计算单个节点 adjustment 对 BS-NL-RES / BS-EQ 的影响。
 */
export function getRoundingNodeImpact(node: RoundingTargetNode, adjustAmount: number): RoundingNodeImpact {
    if (!adjustAmount) return {netAssetsDelta: 0, eqDelta: 0};

    const pc = (node.parentCode || '').toUpperCase();
    const c = (node.code || '').toUpperCase();

    if (node.statement === 'PL') {
        const eqDelta = PL_EXPENSE_PARENTS.has(pc) ? -adjustAmount : adjustAmount;
        return {netAssetsDelta: 0, eqDelta};
    }

    if (BS_ASSET_PARENTS.has(pc)) {
        return {netAssetsDelta: adjustAmount, eqDelta: 0};
    }
    if (BS_LIABILITY_PARENTS.has(pc)) {
        return {netAssetsDelta: -adjustAmount, eqDelta: 0};
    }
    if (pc === 'BS-EQ' || c.startsWith('BS-EQ-')) {
        return {netAssetsDelta: 0, eqDelta: adjustAmount};
    }

    return {netAssetsDelta: 0, eqDelta: 0};
}

export function computeRemainingDifference(
    initialDifference: number,
    nodeValues: Record<string, number>,
    targetNodes: RoundingTargetNode[]
): number {
    let netAssetsDelta = 0;
    let eqDelta = 0;
    // SINGLE：与改造前一致按 code 索引
    const useYoaKeys = nodesUseYoaKeys(targetNodes);
    const nodeByKey = new Map(
        targetNodes.map(n => [useYoaKeys ? roundingNodeKey(n) : n.code, n])
    );

    for (const [key, amount] of Object.entries(nodeValues)) {
        if (!amount) continue;
        const node = nodeByKey.get(key);
        if (!node) continue;
        const impact = getRoundingNodeImpact(node, amount);
        netAssetsDelta += impact.netAssetsDelta;
        eqDelta += impact.eqDelta;
    }

    return initialDifference + netAssetsDelta - eqDelta;
}

export function isRoundingDraftBalanced(
    initialDifference: number,
    nodeValues: Record<string, number>,
    targetNodes: RoundingTargetNode[]
): boolean {
    return computeRemainingDifference(initialDifference, nodeValues, targetNodes) === 0;
}

export function prepareRoundingTargetNodes(nodes: RoundingTargetNode[]): RoundingTargetNode[] {
    return nodes;
}

/** 从弹窗 nodeValues 构建待保存条目 */
export function buildRoundingApplyEntries(
    nodeValues: Record<string, number>,
    targetNodes: RoundingTargetNode[]
): RoundingApplyEntry[] {
    const useYoaKeys = nodesUseYoaKeys(targetNodes);
    const nodeByKey = new Map(
        targetNodes.map(n => [useYoaKeys ? roundingNodeKey(n) : n.code, n])
    );
    return Object.entries(nodeValues)
        .filter(([, amount]) => amount !== 0)
        .map(([key, adjustAmount]) => {
            const node = nodeByKey.get(key);
            return node ? {node, adjustAmount} : null;
        })
        .filter((entry): entry is RoundingApplyEntry => entry !== null);
}

/** 从聚合 VO 展开 targets 列表 */
export function flattenRoundingTargets(
    group: RoundingAdjustGroupVO | null | undefined
): RoundingAdjustTarget[] {
    return group?.targets ?? [];
}

export function hasRoundingTargets(group: RoundingAdjustGroupVO | null | undefined): boolean {
    return (group?.targets?.length ?? 0) > 0;
}

/** 从弹窗 Apply 条目构建 save 请求的 targets */
export function buildRoundingSaveTargets(entries: RoundingApplyEntry[]): RoundingAdjustTargetBO[] {
    return entries.map(entry => {
        const base: RoundingAdjustTargetBO = {
            targetFsId: entry.node.fsId,
            targetCode: entry.node.code,
            targetStatement: entry.node.statement,
            adjustAmount: entry.adjustAmount,
        };
        // 仅 MULTI·PL 带 YOA；SINGLE / BS 不传该字段（与改造前 payload 一致）
        const yoa = entry.node.statement === 'PL'
            ? normalizeRoundingYoa(entry.node.yearOfAssessment)
            : null;
        if (yoa) {
            base.yearOfAssessment = yoa;
        }
        return base;
    });
}

/** 从后端聚合记录构建 BS/PL 显示 map 与弹窗回显数据 */
export function buildRoundingAdjustmentMaps(
    group: RoundingAdjustGroupVO | null | undefined,
    options?: BuildRoundingMapsOptions
): RoundingAdjustmentMaps {
    const empty: RoundingAdjustmentMaps = {
        bsDisplayMap: {},
        plDisplayMap: {},
        sourceMap: {},
        savedNodeValues: {},
    };
    const records = flattenRoundingTargets(group);
    if (records.length === 0) return empty;

    const yoaFilter = normalizeRoundingYoa(options?.yearOfAssessment);
    const hasAnyYoa = records.some(r => !!normalizeRoundingYoa(r.yearOfAssessment));

    // SINGLE / legacy：无 YOA 过滤且记录无 YOA → 与改造前完全一致（savedNodeValues 按 targetCode）
    if (!yoaFilter && !hasAnyYoa) {
        const bsDisplayMap: Record<string, number> = {};
        const plDisplayMap: Record<string, number> = {};
        const sourceMap: Record<string, 'BS' | 'PL'> = {};
        const savedNodeValues: Record<string, number> = {};
        const lfpCode = FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0];

        for (const r of records) {
            savedNodeValues[r.targetCode] = (savedNodeValues[r.targetCode] ?? 0) + r.adjustAmount;

            if (r.targetStatement === 'PL') {
                plDisplayMap[r.targetCode] = (plDisplayMap[r.targetCode] ?? 0) + r.adjustAmount;
                const parts = r.targetCode.split('-');
                const parentCode = parts.length >= 3 ? parts.slice(0, 2).join('-').toUpperCase() : r.targetCode.toUpperCase();
                const effectiveAmount = PL_EXPENSE_PARENTS.has(parentCode) ? -r.adjustAmount : r.adjustAmount;
                bsDisplayMap[lfpCode] = (bsDisplayMap[lfpCode] ?? 0) + effectiveAmount;
                sourceMap[lfpCode] = 'PL';
            } else {
                bsDisplayMap[r.targetCode] = (bsDisplayMap[r.targetCode] ?? 0) + r.adjustAmount;
                sourceMap[r.targetCode] = 'BS';
            }
        }

        return {bsDisplayMap, plDisplayMap, sourceMap, savedNodeValues};
    }

    // MULTI：按 YOA 过滤 / 复合键回显
    const bsDisplayMap: Record<string, number> = {};
    const plDisplayMap: Record<string, number> = {};
    const sourceMap: Record<string, 'BS' | 'PL'> = {};
    const savedNodeValues: Record<string, number> = {};
    const lfpCode = FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0];

    for (const r of records) {
        const rowYoa = normalizeRoundingYoa(r.yearOfAssessment);
        const nodeKey = roundingNodeKey({
            code: r.targetCode,
            statement: r.targetStatement,
            yearOfAssessment: rowYoa,
        });
        savedNodeValues[nodeKey] = (savedNodeValues[nodeKey] ?? 0) + r.adjustAmount;

        if (r.targetStatement === 'PL') {
            if (!targetMatchesYoaFilter(r, yoaFilter)) continue;
            plDisplayMap[r.targetCode] = (plDisplayMap[r.targetCode] ?? 0) + r.adjustAmount;
            const parts = r.targetCode.split('-');
            const parentCode = parts.length >= 3 ? parts.slice(0, 2).join('-').toUpperCase() : r.targetCode.toUpperCase();
            const effectiveAmount = PL_EXPENSE_PARENTS.has(parentCode) ? -r.adjustAmount : r.adjustAmount;
            bsDisplayMap[lfpCode] = (bsDisplayMap[lfpCode] ?? 0) + effectiveAmount;
            sourceMap[lfpCode] = 'PL';
        } else {
            bsDisplayMap[r.targetCode] = (bsDisplayMap[r.targetCode] ?? 0) + r.adjustAmount;
            sourceMap[r.targetCode] = 'BS';
        }
    }

    return {bsDisplayMap, plDisplayMap, sourceMap, savedNodeValues};
}

export interface PlRoundingLookupMaps {
    byCode: Record<string, number>;
    byFsId: Record<number, number>;
}

/** 将多条 PL rounding 记录按 fsId / code 分别汇总（支持同节点多笔、多节点各自累加） */
export function buildPlRoundingLookupMaps(
    group: RoundingAdjustGroupVO | null | undefined,
    options?: BuildRoundingMapsOptions
): PlRoundingLookupMaps {
    const byCode: Record<string, number> = {};
    const byFsId: Record<number, number> = {};
    const records = flattenRoundingTargets(group);
    if (records.length === 0) return {byCode, byFsId};

    const yoaFilter = normalizeRoundingYoa(options?.yearOfAssessment);
    // 无 YOA 过滤时与改造前一致：不过滤
    for (const r of records) {
        if (r.targetStatement !== 'PL') continue;
        if (yoaFilter && !targetMatchesYoaFilter(r, yoaFilter)) continue;
        const amount = Number(r.adjustAmount) || 0;
        if (!amount) continue;
        const fsId = Number(r.targetFsId);
        const code = normalizeCode(r.targetCode);
        if (fsId) {
            byFsId[fsId] = (byFsId[fsId] ?? 0) + amount;
        }
        if (code) {
            byCode[code] = (byCode[code] ?? 0) + amount;
        }
    }
    return {byCode, byFsId};
}

/** 获取节点对应的 PL rounding 调整量（优先 fsId，多条记录已预先汇总） */
export function getPlRoundingAmount(
    node: { fsId?: number | string; code?: string } | null | undefined,
    maps: PlRoundingLookupMaps
): number {
    if (!node) return 0;
    const fsId = Number(node.fsId);
    const code = normalizeCode(node.code);
    if (fsId && maps.byFsId[fsId] != null) return maps.byFsId[fsId];
    return maps.byCode[code] ?? 0;
}

/** 递归汇总节点及子树的 PL rounding（用于父节点 / 派生公式） */
export function sumPlRoundingForNode(
    node: any,
    maps: PlRoundingLookupMaps,
    getChildren: (node: any) => any[] = (n) => (Array.isArray(n?.children) ? n.children : [])
): number {
    const kids = getChildren(node);
    let sum = 0;
    if (kids.length > 0) {
        for (const ch of kids) sum += sumPlRoundingForNode(ch, maps, getChildren);
    }
    sum += getPlRoundingAmount(node, maps);
    return sum;
}

const ROUNDING_DIFF_CHANGE_EVENT = 'fs-rounding-diff-change';
const ROUNDING_SAVED_EVENT = 'fs-rounding-adjust-saved';
const ROUNDING_DIALOG_CANCELLED_EVENT = 'fs-rounding-adjust-cancelled';

let latestRoundingDifference = 0;

export function setLatestRoundingDifference(diff: number): void {
    latestRoundingDifference = diff;
    window.dispatchEvent(new CustomEvent(ROUNDING_DIFF_CHANGE_EVENT, {detail: diff}));
    processRoundingGate(diff);
}

export function getLatestRoundingDifference(): number {
    return latestRoundingDifference;
}

/** 离开 Audited(Round) 时重置，避免下次 gate 使用陈旧差额 */
export function resetLatestRoundingDifference(): void {
    latestRoundingDifference = 0;
}

export function subscribeRoundingDifference(handler: (diff: number) => void): () => void {
    const listener = (e: Event) => handler((e as CustomEvent<number>).detail ?? 0);
    window.addEventListener(ROUNDING_DIFF_CHANGE_EVENT, listener);
    return () => window.removeEventListener(ROUNDING_DIFF_CHANGE_EVENT, listener);
}

export function notifyRoundingSaved(): void {
    if (roundingGate?.source === 'tax' && roundingGate.active) {
        awaitingTaxAfterSave = true;
        requestAnimationFrame(() => processRoundingGate(latestRoundingDifference));
    }
    window.dispatchEvent(new CustomEvent(ROUNDING_SAVED_EVENT));
}

export function subscribeRoundingSaved(handler: () => void): () => void {
    window.addEventListener(ROUNDING_SAVED_EVENT, handler);
    return () => window.removeEventListener(ROUNDING_SAVED_EVENT, handler);
}

export function notifyRoundingDialogCancelled(): void {
    if (roundingGate?.source === 'tax' && roundingGate.active) {
        awaitingTaxAfterSave = false;
        const onCancelled = taxGateOnCancelled;
        clearRoundingGateState();
        onCancelled?.();
    }
    window.dispatchEvent(new CustomEvent(ROUNDING_DIALOG_CANCELLED_EVENT));
}

export function subscribeRoundingDialogCancelled(handler: () => void): () => void {
    window.addEventListener(ROUNDING_DIALOG_CANCELLED_EVENT, handler);
    return () => window.removeEventListener(ROUNDING_DIALOG_CANCELLED_EVENT, handler);
}

// ---------------------------------------------------------------------------
// Unified rounding gate: Audited 列头 / Tax Com / 警告图标 共用一套差额判断与弹窗逻辑
// ---------------------------------------------------------------------------

export type RoundingGateSource = 'audited' | 'tax';

export interface RoundingGateController {
    /** 打开 RoundingAdjustmentDialog（由 BalanceSheet 注册） */
    openDialog: () => void;
    /** 差额为 0 或无需弹窗时关闭（由 BalanceSheet 注册） */
    closeDialog?: () => void;
    /** BS/PL rounded 与 rounding 数据是否就绪，可计算最终差额 */
    isDataReady: () => boolean;
    /** 用户已 Cancel，本轮不再自动弹窗 */
    isDismissed: () => boolean;
    clearDismissed: () => void;
}

type ActiveRoundingGate = {
    source: RoundingGateSource;
    active: boolean;
};

let gateController: RoundingGateController | null = null;
let roundingGate: ActiveRoundingGate | null = null;
let taxGateOnBalanced: (() => void) | null = null;
let taxGateOnCancelled: (() => void) | null = null;
let awaitingTaxAfterSave = false;

function clearRoundingGateState(): void {
    roundingGate = null;
    taxGateOnBalanced = null;
    taxGateOnCancelled = null;
    awaitingTaxAfterSave = false;
}

function processRoundingGate(diff: number): void {
    if (!roundingGate?.active || !gateController) return;
    if (!gateController.isDataReady()) return;
    if (gateController.isDismissed()) return;

    if (awaitingTaxAfterSave && roundingGate.source === 'tax') {
        if (diff !== 0) return;
        const onBalanced = taxGateOnBalanced;
        clearRoundingGateState();
        gateController.closeDialog?.();
        requestAnimationFrame(() => onBalanced?.());
        return;
    }

    if (diff === 0) {
        gateController.closeDialog?.();
        if (roundingGate.source === 'tax') {
            const onBalanced = taxGateOnBalanced;
            clearRoundingGateState();
            requestAnimationFrame(() => onBalanced?.());
        } else {
            clearRoundingGateState();
        }
        return;
    }

    gateController.openDialog();
    if (roundingGate.source === 'audited') {
        clearRoundingGateState();
    }
}

/** 数据就绪后重试当前 gate（如 sPLRoundedAfterTax 刚写入 store） */
export function retryActiveRoundingGate(): void {
    if (!roundingGate?.active) return;
    processRoundingGate(latestRoundingDifference);
}

export function registerRoundingGateController(controller: RoundingGateController): void {
    gateController = controller;
}

export function unregisterRoundingGateController(): void {
    gateController = null;
}

/** Audited 列头 normal→round、警告图标点击 */
export function requestAuditedRoundingGate(): void {
    gateController?.clearDismissed();
    awaitingTaxAfterSave = false;
    taxGateOnBalanced = null;
    taxGateOnCancelled = null;
    roundingGate = {source: 'audited', active: true};
    processRoundingGate(latestRoundingDifference);
}

export interface TaxRoundingGateOptions {
    onBalanced: () => void;
    onCancelled?: () => void;
}

/** Tax Com：差额为 0 则 onBalanced；否则弹窗，保存后再 onBalanced */
export function requestTaxRoundingGate(options: TaxRoundingGateOptions): void {
    gateController?.clearDismissed();
    awaitingTaxAfterSave = false;
    taxGateOnBalanced = options.onBalanced;
    taxGateOnCancelled = options.onCancelled ?? null;
    roundingGate = {source: 'tax', active: true};
    processRoundingGate(latestRoundingDifference);
}

/** @deprecated 使用 requestAuditedRoundingGate */
export function notifyAuditedRoundModeEntered(): void {
    requestAuditedRoundingGate();
}

/** @deprecated 使用 requestAuditedRoundingGate */
export function requestOpenRoundingDialog(): void {
    requestAuditedRoundingGate();
}
